/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/react-native
 * @description Renders the native single-domain adapter detail view.
 */

import type {
  AdapterDomain,
  DomainAdapterConfig,
  ProviderMeta,
} from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
  LAUNCH_DB_PROVIDERS,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useState } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { ErrorBoundaryBridge } from "../bridges/ErrorBoundaryBridge";
import { semanticColors } from "../bridges/TokenBridge";
import { AdapterConfigForm, AdapterStatusBadge } from "../components";
import { useAdapterStatus, useAdapters } from "../hooks";
import { Pressable, ScrollView, StyleSheet, Text, View } from "../lib/native";

/**
 * Props for the native single-domain adapter detail page.
 *
 * @property domain Adapter domain shown by the detail page.
 * @property onBack Optional callback used to render and handle the back action.
 * @property onConfigure Optional callback invoked with submitted adapter configuration.
 * @property style Optional native style merged onto the page container.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Renders provider metadata, status, and configuration for one adapter domain.
 *
 * @param props Detail page props including the target domain and optional back action.
 * @returns A React Native detail page for the requested adapter domain.
 * @example
 * ```tsx
 * <AdapterDetailPage domain="db" onBack={() => navigation.goBack()} />
 * ```
 */
export function AdapterDetailPage({
  domain,
  onBack,
  onConfigure,
  style,
  testID,
}: AdapterDetailPageProps): JSX.Element {
  return (
    <ErrorBoundaryBridge style={style} testID="adapter-detail-error-boundary">
      <AdapterDetailPageContent
        domain={domain}
        onBack={onBack}
        onConfigure={onConfigure}
        style={style}
        testID={testID}
      />
    </ErrorBoundaryBridge>
  );
}

function AdapterDetailPageContent({
  domain,
  onBack,
  onConfigure,
  style,
  testID,
}: AdapterDetailPageProps): JSX.Element {
  const { isLoading } = useAdapters();
  const status = useAdapterStatus(domain);
  const [isEditing, setIsEditing] = useState(false);
  const providers: ProviderMeta[] = getProvidersForDomain(domain);
  const launchSelectableIds = new Set(
    domain === "db"
      ? ["localStorage", ...LAUNCH_DB_PROVIDERS]
      : providers.map((provider) => provider.id),
  );

  if (isLoading) {
    return (
      <View
        accessibilityLabel="Loading adapter details"
        accessibilityRole="progressbar"
        style={[styles.page, style]}
        testID={testID ?? "adapter-detail-loading"}
      >
        <View style={styles.skeletonTitle} />
        <View style={styles.skeletonBlock} />
        <View style={styles.skeletonBlock} />
      </View>
    );
  }

  return (
    <ScrollView
      accessibilityLabel={`${DOMAIN_LABELS[domain]} adapter details`}
      contentContainerStyle={[styles.page, style]}
      keyboardShouldPersistTaps="handled"
      testID={testID ?? "adapter-detail-page"}
    >
      {onBack && (
        <Pressable
          accessibilityLabel="Back to adapters"
          accessibilityRole="button"
          onPress={onBack}
          style={styles.backButton}
          testID="adapter-detail-back"
        >
          <Text style={styles.backText}>Back to Adapters</Text>
        </Pressable>
      )}

      <View style={styles.hero}>
        <View style={styles.iconBox}>
          <Text style={styles.iconText}>{DOMAIN_ICONS[domain]}</Text>
        </View>
        <View style={styles.heroBody}>
          <View style={styles.heroTitleRow}>
            <Text accessibilityRole="header" style={styles.title}>
              {DOMAIN_LABELS[domain]}
            </Text>
            <AdapterStatusBadge status={status.status} />
          </View>
          <Text style={styles.description}>{DOMAIN_DESCRIPTIONS[domain]}</Text>
        </View>
      </View>

      <View style={styles.panel}>
        <Text style={styles.sectionLabel}>Current Status</Text>
        <View style={styles.statusGrid}>
          <View style={styles.statusCell}>
            <Text style={styles.statusLabel}>Provider</Text>
            <Text style={styles.statusValue}>
              {status.provider !== "none" ? status.provider : "None"}
            </Text>
          </View>
          <View style={styles.statusCell}>
            <Text style={styles.statusLabel}>Status</Text>
            <AdapterStatusBadge status={status.status} />
          </View>
          <View style={styles.statusCell}>
            <Text style={styles.statusLabel}>Last Checked</Text>
            <Text style={styles.statusValue}>
              {status.lastCheckedAt
                ? new Date(status.lastCheckedAt).toLocaleTimeString()
                : "Not checked"}
            </Text>
          </View>
        </View>
        {status.error && (
          <View accessibilityRole="alert" style={styles.errorBox}>
            <Text style={styles.errorText}>{status.error}</Text>
          </View>
        )}
      </View>

      <View style={styles.panel}>
        <Text style={styles.sectionLabel}>Available Providers</Text>
        <View style={styles.providers}>
          {providers.map((provider: ProviderMeta) => {
            const isAvailable = launchSelectableIds.has(provider.id);
            return (
              <View key={provider.id} style={styles.providerRow}>
                <View style={styles.providerBody}>
                  <Text style={styles.providerName}>{provider.name}</Text>
                  <Text style={styles.providerDescription}>
                    {provider.description}
                  </Text>
                </View>
                <View style={styles.providerBadges}>
                  <Text style={styles.providerTier}>{provider.tier}</Text>
                  {!isAvailable && (
                    <Text
                      accessibilityLabel={`${provider.name} is unavailable`}
                      style={styles.unavailableBadge}
                      testID={`provider-unavailable-${provider.id}`}
                    >
                      Unavailable
                    </Text>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </View>

      {isEditing ? (
        <AdapterConfigForm
          domain={domain}
          onCancel={() => setIsEditing(false)}
          onSave={async (config) => {
            await onConfigure?.(domain, config);
            setIsEditing(false);
          }}
        />
      ) : (
        <Pressable
          accessibilityLabel={`Configure ${DOMAIN_LABELS[domain]} adapter`}
          accessibilityRole="button"
          onPress={() => setIsEditing(true)}
          style={styles.configureButton}
          testID="adapter-detail-configure"
        >
          <Text style={styles.configureText}>
            Configure {DOMAIN_LABELS[domain]} Adapter
          </Text>
        </Pressable>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  backButton: {
    alignSelf: "flex-start",
    paddingVertical: 4,
  },
  backText: {
    color: semanticColors.surface.textSoft,
    fontSize: 14,
    fontWeight: "700",
  },
  configureButton: {
    alignItems: "center",
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.borderStrong,
    borderRadius: 10,
    borderStyle: "dashed",
    borderWidth: 1,
    padding: 18,
  },
  configureText: {
    color: semanticColors.surface.textSoft,
    fontSize: 14,
    fontWeight: "800",
  },
  description: {
    color: semanticColors.surface.textMuted,
    fontSize: 14,
    lineHeight: 20,
    marginTop: 4,
  },
  errorBox: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 12,
    padding: 12,
  },
  errorText: {
    color: semanticColors.status.error.text,
    fontSize: 13,
    fontWeight: "600",
  },
  hero: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: 14,
  },
  heroBody: {
    flex: 1,
  },
  heroTitleRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  iconBox: {
    alignItems: "center",
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 12,
    borderWidth: 1,
    height: 58,
    justifyContent: "center",
    width: 58,
  },
  iconText: {
    fontSize: 28,
  },
  page: {
    backgroundColor: semanticColors.surface.bg,
    gap: 18,
    padding: 16,
  },
  panel: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    padding: 16,
  },
  providerBody: {
    flex: 1,
  },
  providerDescription: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 3,
  },
  providerName: {
    color: semanticColors.surface.text,
    fontSize: 14,
    fontWeight: "800",
  },
  providerRow: {
    alignItems: "flex-start",
    borderBottomColor: semanticColors.surface.border,
    borderBottomWidth: 1,
    flexDirection: "row",
    gap: 12,
    paddingVertical: 12,
  },
  providerTier: {
    backgroundColor: semanticColors.status.initializing.bg,
    borderRadius: 6,
    color: semanticColors.status.initializing.text,
    fontSize: 10,
    fontWeight: "800",
    overflow: "hidden",
    paddingHorizontal: 8,
    paddingVertical: 3,
    textTransform: "uppercase",
  },
  providers: {
    marginTop: 6,
  },
  providerBadges: {
    alignItems: "flex-end",
    gap: 4,
  },
  sectionLabel: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  skeletonBlock: {
    backgroundColor: semanticColors.surface.skeleton,
    borderRadius: 12,
    height: 120,
  },
  skeletonTitle: {
    backgroundColor: semanticColors.surface.skeletonStrong,
    borderRadius: 999,
    height: 24,
    width: "45%",
  },
  statusCell: {
    flex: 1,
    minWidth: 120,
  },
  statusGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 12,
  },
  statusLabel: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    textTransform: "uppercase",
  },
  statusValue: {
    color: semanticColors.surface.text,
    fontSize: 14,
    fontWeight: "700",
    marginTop: 5,
  },
  title: {
    color: semanticColors.surface.text,
    fontSize: 25,
    fontWeight: "900",
  },
  unavailableBadge: {
    backgroundColor: semanticColors.surface.muted,
    borderColor: semanticColors.surface.border,
    borderRadius: 6,
    borderWidth: 1,
    color: semanticColors.surface.textMuted,
    fontSize: 10,
    fontWeight: "700",
    overflow: "hidden",
    paddingHorizontal: 8,
    paddingVertical: 3,
    textTransform: "uppercase",
  },
});
